import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

const ShippingAdminPage: React.FC = () => {
  const [methods, setMethods] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    estimated_days: '',
    is_active: true,
  });

  useEffect(() => {
    fetchMethods();
  }, []);

  const fetchMethods = async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getAllShipping();
      if (res.success && res.data) setMethods(res.data);
    } catch {
      toast.error('Failed to fetch shipping methods');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
      };
      if (editingId) {
        await adminAPI.updateShipping(editingId, data);
        toast.success('Shipping method updated');
      } else {
        await adminAPI.createShipping(data);
        toast.success('Shipping method created');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', description: '', price: '', estimated_days: '', is_active: true });
      fetchMethods();
    } catch {
      toast.error('Failed to save shipping method');
    }
  };

  const handleEdit = (method: any) => {
    setEditingId(method.id);
    setFormData({
      name: method.name,
      description: method.description || '',
      price: String(method.price),
      estimated_days: method.estimated_days || '',
      is_active: method.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this shipping method?')) return;
    try {
      await adminAPI.deleteShipping(id);
      toast.success('Shipping method deleted');
      fetchMethods();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
          <h1 className="font-serif text-3xl text-primary">Shipping Methods</h1>
        </div>
        <button
          onClick={() => {
            setEditingId(null);
            setFormData({ name: '', description: '', price: '', estimated_days: '', is_active: true });
            setShowForm(!showForm);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={14} />
          <span>Add Method</span>
        </button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-light p-6"
        >
          <h2 className="font-serif text-xl text-primary mb-6">
            {editingId ? 'Edit Shipping Method' : 'New Shipping Method'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">Name</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-luxury"
                required
                placeholder="Standard Shipping"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Price ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="input-luxury"
                required
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Estimated Days
              </label>
              <input
                value={formData.estimated_days}
                onChange={(e) => setFormData({ ...formData, estimated_days: e.target.value })}
                className="input-luxury"
                placeholder="3-5 business days"
              />
            </div>
            <div className="flex items-center pt-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="accent-primary"
                />
                <span className="text-xs tracking-widest uppercase">Active</span>
              </label>
            </div>
            <div className="col-span-2">
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Description
              </label>
              <input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-luxury"
                placeholder="Description (optional)"
              />
            </div>
            <div className="col-span-2 flex space-x-3">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update Method' : 'Create Method'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="bg-white border border-light overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light">
              {['Name', 'Price', 'Estimated Days', 'Status', 'Actions'].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center">
                  <div className="skeleton h-6 w-48 mx-auto rounded" />
                </td>
              </tr>
            ) : methods.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase">
                  No shipping methods
                </td>
              </tr>
            ) : (
              methods.map((method) => (
                <tr key={method.id} className="border-b border-light hover:bg-light/50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="text-xs font-medium tracking-wide">{method.name}</p>
                    {method.description && (
                      <p className="text-xs text-nude mt-1">{method.description}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs">
                    {Number(method.price) === 0 ? 'Free' : `$${Number(method.price).toFixed(2)}`}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {method.estimated_days || '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                      method.is_active
                        ? 'bg-green-50 text-green-600'
                        : 'bg-red-50 text-red-500'
                    }`}>
                      {method.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEdit(method)}
                        className="text-nude hover:text-primary transition-colors"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(method.id)}
                        className="text-nude hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShippingAdminPage;